import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { api, resolveImage, mailto } from "../lib/api";
import ClassicCard from "../components/ClassicCard";
import Seo from "../components/Seo";

export default function ClassicDetail() {
  const { slug } = useParams();
  const { lang, t } = useI18n();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    (async () => {
      try {
        const r = await api.get(`/classics/${slug}`);
        setData(r.data);
      } catch (e) {
        if (e.response?.status === 404) setNotFound(true);
        console.error(e);
      }
    })();
  }, [slug]);

  if (notFound) {
    return (
      <div className="stoel-container py-32 text-center">
        <p className="text-stoel-inkSoft">404</p>
        <Link to="/klassiekers" className="stoel-link mt-4 inline-block">
          {t.common.backTo} {t.common.classics}
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="stoel-container py-32">
        <div className="animate-pulse grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7 aspect-[4/5] bg-stoel-paperDeep" />
          <div className="md:col-span-5 space-y-4 pt-10">
            <div className="h-12 w-3/4 bg-stoel-paperDeep" />
            <div className="h-4 w-1/2 bg-stoel-paperDeep" />
            <div className="h-3 w-full bg-stoel-paperDeep mt-8" />
            <div className="h-3 w-full bg-stoel-paperDeep" />
            <div className="h-3 w-5/6 bg-stoel-paperDeep" />
          </div>
        </div>
      </div>
    );
  }

  const item = data.item;
  const related = data.related || [];
  const short = lang === "nl" ? item.short_nl : item.short_en;
  const long = lang === "nl" ? item.long_nl : item.long_en;
  const category = t.categories[item.category] || item.category;

  return (
    <div data-testid="page-classic-detail" className="stoel-container pt-8 md:pt-12 pb-24">
      <Seo
        path={`/klassiekers/${item.slug}`}
        titleNl={`${item.name} — ${item.designer}`}
        titleEn={`${item.name} — ${item.designer}`}
        descNl={short || `${item.name}, ontworpen door ${item.designer}${item.year ? ` (${item.year})` : ""}. ${category}.`}
        descEn={short || `${item.name}, designed by ${item.designer}${item.year ? ` (${item.year})` : ""}. ${category}.`}
        ogImage={item.image_url ? `https://destoel.com${item.image_url}` : undefined}
        ogType="article"
      />
      <Link
        to="/klassiekers"
        data-testid="back-to-classics"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-stoel-walnut hover:text-stoel-terracotta mb-8"
      >
        <ArrowLeft size={14} /> {t.common.backTo} {t.common.classics}
      </Link>

      <div className="grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-7">
          <div className="relative aspect-[4/5] overflow-hidden bg-stoel-paperDeep">
            <img
              src={resolveImage(item.image_url)}
              alt={item.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.src = resolveImage(null);
              }}
            />
          </div>
        </div>

        <div className="md:col-span-5 md:sticky md:top-28 md:self-start">
          <div className="stoel-eyebrow mb-4">{category}</div>
          <h1
            data-testid="classic-title"
            className="stoel-display text-4xl md:text-5xl lg:text-6xl"
          >
            {item.name}
          </h1>

          <dl className="mt-8 space-y-3 text-sm">
            <Row label={t.common.designer} value={item.designer} />
            {item.brand && <Row label={t.common.brand} value={item.brand} />}
            {item.year && <Row label={t.common.year} value={item.year} />}
          </dl>

          {short && (
            <p className="mt-8 text-lg text-stoel-ink font-light leading-relaxed">
              {short}
            </p>
          )}

          {long && (
            <p className="mt-6 text-base text-stoel-inkSoft leading-relaxed whitespace-pre-line">
              {long}
            </p>
          )}

          <a
            href={mailto(`Interesse: ${item.name}`)}
            data-testid="classic-inquire-cta"
            className="mt-10 inline-flex items-center gap-3 px-6 py-3.5 bg-stoel-terracotta text-stoel-paper hover:bg-stoel-terracottaDeep transition-colors text-sm tracking-wide"
          >
            {t.common.inquire}: {item.name} <ArrowUpRight size={16} />
          </a>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-24 md:mt-32">
          <div className="flex items-baseline justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-light tracking-tight">
              {t.common.related}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-10">
            {related.map((c, i) => (
              <ClassicCard key={c.id} item={c} index={i} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex border-b border-stoel-line pb-2">
      <dt className="w-32 text-stoel-walnut uppercase text-xs tracking-[0.18em] pt-0.5">
        {label}
      </dt>
      <dd className="flex-1 text-stoel-ink">{value}</dd>
    </div>
  );
}
